import { MapPin, Clock, Phone, Mail } from "lucide-react";
import { contactInfo } from "@/data/machines";
import Reveal, { Stagger, RevealItem } from "@/components/site/Reveal";

const rows = [
  {
    icon: MapPin,
    label: "Dirección",
    value: (
      <>
        {contactInfo.addressLine1}
        <br />
        {contactInfo.addressLine2}
      </>
    ),
    href: null,
  },
  { icon: Clock, label: "Horario", value: contactInfo.schedule, href: null },
  {
    icon: Phone,
    label: "Teléfono",
    value: (
      <>
        <a href={`tel:${contactInfo.phoneRaw}`} className="block hover:text-sky-600 transition-colors">
          {contactInfo.phone}
        </a>
        <span className="block">{contactInfo.phone2}</span>
      </>
    ),
    href: null,
    testId: "contact-phone",
  },
  {
    icon: Mail,
    label: "Email",
    value: contactInfo.email,
    href: `mailto:${contactInfo.email}`,
    testId: "contact-email",
    break: true,
  },
];

export default function Contact() {
  return (
    <section id="contacto" data-testid="contact-section" className="py-24 md:py-28 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-sky-600">
                <span className="w-6 h-px bg-sky-500" /> Dónde nos encontramos
              </span>
              <h2 className="font-heading mt-4 text-3xl md:text-4xl font-bold tracking-tight text-slate-950 leading-[1.1]">
                Pasa a vernos o llámanos
              </h2>
              <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed">
                Nos encontrarás en el <span className="font-semibold text-slate-800">Polígono Industrial S, 30 P, Santa Olalla (Toledo)</span>.
                Buen acceso para furgonetas y remolques.
              </p>
            </Reveal>

            <Stagger className="mt-10 space-y-4">
              {rows.map((r) => {
                const Icon = r.icon;
                const inner = (
                  <>
                    <div className="w-11 h-11 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center shrink-0 transition-all duration-300 group-hover:bg-sky-500 group-hover:text-white group-hover:border-sky-500 group-hover:scale-105">
                      <Icon className="w-5 h-5 text-sky-600 transition-colors duration-300 group-hover:text-white" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        {r.label}
                      </div>
                      <div
                        className={`font-medium text-slate-900 mt-0.5 ${r.break ? "break-all" : ""}`}
                      >
                        {r.value}
                      </div>
                    </div>
                  </>
                );
                return (
                  <RevealItem key={r.label}>
                    {r.href ? (
                      <a
                        href={r.href}
                        data-testid={r.testId}
                        className="group flex items-start gap-4 hover:-translate-x-0.5 transition-transform duration-300"
                      >
                        {inner}
                      </a>
                    ) : (
                      <div className="group flex items-start gap-4">{inner}</div>
                    )}
                  </RevealItem>
                );
              })}
            </Stagger>
          </div>

          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-[0_20px_60px_-20px_rgba(2,32,71,0.25)] h-[420px] md:h-[520px] bg-slate-100">
              <iframe
                title="Ubicación"
                data-testid="contact-map"
                src={`https://maps.google.com/maps?q=${contactInfo.mapLat},${contactInfo.mapLng}&z=17&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div aria-hidden className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-sky-500/10 rounded-3xl" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
